// ─────────────────────────────────────────────────────────────────────────────
// REQ-104 — Post-it interactivo / "Word Builder" para el reconocimiento DIARIO.
//
// Rediseño 2026-08-05 (ver docs/superpowers/specs/2026-08-05-postit-interactivo-rediseno-design.md):
// un solo árbol de UI para todos los tamaños — banco de palabras en pestañas (WordBankTabs) debajo
// de un post-it central (PostItCard) con personalización y envío integrados, guía flotante
// (GuideOverlay) que se muestra la primera vez o al pedirla, y un dial de progreso (ProgressDial)
// en vez del contador de texto y la columna fija de "cómo funciona". Reemplaza el wizard móvil de
// 5 pantallas y el popup de envío de la versión anterior.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRecognitionCatalog,
  useCreateRecognition,
  useRecognitionRules,
  useNextBrillamos,
  getFundamentalName,
  getFundamentalColor,
  getBehaviorName,
  getHabitName,
  availableHabitsFor,
} from '@/api/recognition';
import type {
  PersonSummary,
  FundamentalDto,
  AttitudeBehaviorDto,
  DailyHabitDto,
  CreateRecognitionBody,
} from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { ProgressDial } from '@/components/common/ProgressDial';
import { GuideOverlay, type GuideStep } from '@/components/common/GuideOverlay';
import { StateBlock } from './components';
import { recognitionSendErrorMessage } from './messageLimit';
import { WordBankTabs, type BankTab } from './WordBankTabs';
import { PostItCard, type SlotId } from './PostItCard';
import { fundamentalesOfrecibles } from './postitShared';
import { useVistaHabilitada } from './useVistas';

const IMPACT_KEYS = ['impact1', 'impact2', 'impact3', 'impact4'] as const;
const GUIDE_STORAGE_KEY = 'ri_guide_seen';
const SENT_MS = 2200;

interface Props {
  onSent?: () => void;
  initialRecipients?: PersonSummary[];
  /** RN-PI03 — cuando el post-it se monta en /app/brillamos: id de la campaña activa (viaja como
   *  `campanaId`) y bandera para etiquetar el origen. Sin ellas, el envío es 'Diario' (Fundamentales). */
  campaignId?: string | null;
  isBrillamosScreen?: boolean;
}

type TranslateFn = (key: string) => string;

interface SentenceParts {
  recipientLabel: string | null;
  fundLabel: string | null;
  behaviorRequired: boolean;
  behaviorLabel: string | null;
  habitLabel: string | null;
  impactLabel: string | null;
}

interface SlotsProgress {
  behaviorRequired: boolean;
  habitRequired: boolean;
  totalSlots: number;
  filledSlots: number;
  isComplete: boolean;
}

interface ResolvedLabels {
  recipientLabel: string | null;
  fundLabel: string | null;
  fundColor: string | null;
  behaviorLabel: string | null;
  habitLabel: string | null;
  impactLabel: string | null;
}

// Helpers de módulo — extraídos fuera de `WordBuilderPanel` (mismo criterio que `PostItCard` en
// Task 5) para mantener su complejidad cognitiva bajo el límite del lint: toda decisión (ternario,
// if, &&/||) que puede resolverse sin cerrar sobre los setters de estado vive aquí, como función
// pura. El grueso de la complejidad original venía de los ~15 ternarios/operadores lógicos de los
// slots (`behaviorRequired`/`totalSlots`/`filledSlots`/`isComplete`) y las 6 etiquetas derivadas
// (`fundLabel`, `behaviorLabel`, …) — verificado de forma empírica con `eslint --no-cache` sobre
// versiones reducidas del archivo: ese bloque solo, sin nada más, ya reportaba complejidad 18.

/** Fundamentales/Comportamientos/Hábitos activos filtrados desde el catálogo crudo. */
function computeActiveFundamentals(data: FundamentalDto[] | undefined): FundamentalDto[] {
  return (data ?? []).filter((fund) => fund.isActive);
}
function computeActiveBehaviors(fund: FundamentalDto | null): AttitudeBehaviorDto[] {
  return (fund?.attitudes ?? []).filter((attitude) => attitude.isActive);
}
function computeHabits(fund: FundamentalDto | null, behaviorId: string | null): DailyHabitDto[] {
  if (!fund) return [];
  return availableHabitsFor(fund, behaviorId ? [behaviorId] : []);
}

/** Próxima pestaña del banco tras elegir un Fundamental, o `null` si debe quedarse en la actual.
 *  Solo avanza si la categoría destino tiene opciones: de lo contrario la palabra recién elegida
 *  quedaría visible solo en el post-it, sin su contraparte en el banco. */
function nextTabAfterFundamental(fund: FundamentalDto | undefined): BankTab | null {
  if (!fund) return null;
  if (computeActiveBehaviors(fund).length > 0) return 'comportamientos';
  return computeHabits(fund, null).length > 0 ? 'habitos' : null;
}

/** Próxima pestaña del banco tras elegir un comportamiento, o `null` si debe quedarse en la
 *  actual (mismo criterio que `nextTabAfterFundamental`). */
function nextTabAfterBehavior(fund: FundamentalDto | null, behaviorId: string): BankTab | null {
  return computeHabits(fund, behaviorId).length > 0 ? 'habitos' : null;
}

/** Guarda de solo-mostrar-una-vez de la guía: `localStorage` no existe en todos los entornos
 *  (SSR/tests), así que la condición se resuelve aquí en una única expresión. */
function shouldShowGuideOnMount(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !window.localStorage.getItem(GUIDE_STORAGE_KEY);
  } catch {
    return true; // sin almacenamiento disponible: se muestra la guía (comportamiento por defecto)
  }
}

/** Cuántos slots hacen falta, cuántos ya están llenos y si el reconocimiento está listo para
 *  enviarse — comportamiento/hábito solo cuentan como requeridos cuando el Fundamental elegido
 *  realmente los tiene disponibles. */
function computeSlotsProgress(
  selectedFund: FundamentalDto | null,
  hasAttitudes: boolean,
  habitsCount: number,
  recipient: PersonSummary | null,
  fundamentalId: string | null,
  behaviorId: string | null,
  habitId: string | null,
  impactIdx: number | null,
): SlotsProgress {
  const behaviorRequired = !selectedFund || hasAttitudes;
  const habitRequired = !selectedFund || habitsCount > 0;
  const totalSlots = 3 + (behaviorRequired ? 1 : 0) + (habitRequired ? 1 : 0);
  const filledSlots =
    (recipient ? 1 : 0) +
    (fundamentalId ? 1 : 0) +
    (behaviorRequired && behaviorId ? 1 : 0) +
    (habitRequired && habitId ? 1 : 0) +
    (impactIdx !== null ? 1 : 0);
  const isComplete =
    Boolean(recipient) &&
    Boolean(fundamentalId) &&
    (!behaviorRequired || Boolean(behaviorId)) &&
    (!habitRequired || Boolean(habitId)) &&
    impactIdx !== null;
  return { behaviorRequired, habitRequired, totalSlots, filledSlots, isComplete };
}

/** Etiquetas de texto (y color) ya resueltas para cada slot elegido — usadas tanto por el post-it
 *  como por la frase que se manda al backend. */
function resolveLabels(
  t: TranslateFn,
  lang: string,
  recipient: PersonSummary | null,
  selectedFund: FundamentalDto | null,
  fundIdx: number,
  selectedBehavior: AttitudeBehaviorDto | null,
  selectedHabit: DailyHabitDto | null,
  impactIdx: number | null,
): ResolvedLabels {
  return {
    recipientLabel: recipient ? (recipient.displayName ?? recipient.email) : null,
    fundLabel: selectedFund ? getFundamentalName(selectedFund, lang) : null,
    fundColor: selectedFund ? getFundamentalColor(selectedFund, fundIdx) : null,
    behaviorLabel: selectedBehavior ? getBehaviorName(selectedBehavior, lang) : null,
    habitLabel: selectedHabit ? getHabitName(selectedHabit, lang) : null,
    impactLabel: impactIdx !== null ? t(`ri.${IMPACT_KEYS[impactIdx]}`) : null,
  };
}

/** Arma la frase del post-it a partir de las etiquetas ya resueltas de cada slot. */
function buildRecognitionSentence(t: TranslateFn, parts: Readonly<SentenceParts>): string {
  const bits: string[] = [t('ri.templateThanks'), parts.recipientLabel ?? ''];
  bits.push(t('ri.templateForLiving'), parts.fundLabel ?? '');
  if (parts.behaviorRequired && parts.behaviorLabel) bits.push(t('ri.templateWith'), parts.behaviorLabel);
  if (parts.habitLabel) bits.push(t('ri.templateShowing'), parts.habitLabel);
  if (parts.impactLabel) bits.push(`${t('ri.templateThat')} ${parts.impactLabel}.`);
  return bits.filter(Boolean).join(' ');
}

/** Post-it interactivo del reconocimiento diario — orquesta catálogo, estado del formulario y las
 * piezas de UI (`WordBankTabs`, `PostItCard`, `GuideOverlay`, `ProgressDial`). */
export function WordBuilderPanel({ onSent, initialRecipients, campaignId = null, isBrillamosScreen = false }: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const { demoUser } = useUserContext();

  const catalog = useRecognitionCatalog();
  const create = useCreateRecognition();
  // Word Builder es entrada de Brillamos: sus reglas salen del programa Brillamos, no del global.
  const rules = useRecognitionRules(campaignId, isBrillamosScreen ? 'Brillamos' : undefined);
  const anonymousAllowed = rules.data?.anonymousAllowed ?? true;
  // Bug Sesión #95 — misma regla de mínimo de caracteres que ReconocerPanel (formulario clásico);
  // el post-it solo carecía de la NOTA visual, el backend ya la exigía igual en los dos.
  const commentRequired = rules.data?.commentRequired ?? true;
  const minCommentChars = rules.data?.minCommentChars ?? 0;

  // R7 — este panel NO tenía NINGÚN wiring de `sendingDisabled` (ni siquiera el kill-switch global,
  // que ReconocerPanel ya refleja): el post-it dejaba enviar y el servidor rechazaba en el último
  // paso. Mismo patrón que ReconocerPanel — `useVistaHabilitada` ya compone el kill-switch global
  // CON la excepción de localidad `DesactivarEnvio` (fix de backend de esta misma tarea).
  const enviarHabilitado = useVistaHabilitada('enviarReconocimiento', isBrillamosScreen ? 'Brillamos' : undefined);
  const sendingDisabled = !enviarHabilitado;
  // Excepción de horario restringido activa AHORA: bloquea igual que el kill-switch. El tope
  // efectivo es solo informativo (el conteo real del emisor no viaja en este DTO).
  const scheduleRestricted = rules.data?.scheduleRestricted ?? false;
  const weeklySendLimitEffective = rules.data?.weeklySendLimitEffective ?? null;

  // T5 — check "enviar también a Brillamos", mismo criterio que ReconocerPanel (formulario
  // clásico): solo se ofrece si Gestión lo permite Y existe una campaña Brillamos programada a
  // futuro para la OU del emisor. El backend es quien valida/resuelve; aquí solo se decide si
  // OFRECER el check para no mostrarlo "en vano".
  const allowPromoteToBrillamos = rules.data?.allowPromoteToBrillamos ?? false;
  const brillamosGateOu = allowPromoteToBrillamos ? (demoUser.ou || undefined) : undefined;
  const nextBrillamos = useNextBrillamos(brillamosGateOu);
  const showSendToBrillamos = Boolean(brillamosGateOu) && Boolean(nextBrillamos.data);

  const [recipient, setRecipient] = useState<PersonSummary | null>(initialRecipients?.[0] ?? null);
  const [fundamentalId, setFundamentalId] = useState<string | null>(null);
  const [behaviorId, setBehaviorId] = useState<string | null>(null);
  const [habitId, setHabitId] = useState<string | null>(null);
  const [impactIdx, setImpactIdx] = useState<number | null>(null);
  const [hoveredSlot, setHoveredSlot] = useState<SlotId | null>(null);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [sendToBrillamos, setSendToBrillamos] = useState(false);
  const [sent, setSent] = useState(false);
  // Guarda de cambios sin guardar: borrador "sucio" cuando el usuario empezó a construir el
  // reconocimiento (mensaje o cualquier selección) y aún no lo envió. Mismo criterio que ReconocerPanel.
  const hasUnsavedDraft = !sent && (
    message.trim().length > 0
    || fundamentalId !== null
    || behaviorId !== null
    || habitId !== null
    || impactIdx !== null
  );
  useUnsavedChangesGuard(hasUnsavedDraft);
  const [activeTab, setActiveTab] = useState<BankTab>('fundamentales');
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (shouldShowGuideOnMount()) setShowGuide(true);
  }, []);

  const closeGuide = () => {
    setShowGuide(false);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(GUIDE_STORAGE_KEY, '1'); } catch { /* sin almacenamiento */ }
    }
  };

  // WP-F5 — mismo criterio que el panel clásico: al filtro de catálogo (`isActive`, de Pulso) se le
  // suma el del programa (`fundamentalesDeshabilitados`, de Gestión). Las dos puertas de envío tienen
  // que ofrecer lo mismo, o retirar un Fundamental lo escondería en una y no en la otra.
  // `computeActiveFundamentals` ordena y normaliza; `fundamentalesOfrecibles` aplica los dos
  // filtros. El orden importa: primero se normaliza el catálogo, luego se recorta.
  const funds: FundamentalDto[] = useMemo(
    () => fundamentalesOfrecibles(
      computeActiveFundamentals(catalog.data), rules.data?.fundamentalesDeshabilitados),
    [catalog.data, rules.data?.fundamentalesDeshabilitados],
  );
  const fundIdx = fundamentalId ? funds.findIndex((f) => f.id === fundamentalId) : -1;
  const selectedFund = fundIdx >= 0 ? funds[fundIdx] : null;

  const behaviors: AttitudeBehaviorDto[] = useMemo(() => computeActiveBehaviors(selectedFund), [selectedFund]);
  const hasAttitudes = behaviors.length > 0;
  const selectedBehavior = behaviors.find((b) => b.id === behaviorId) ?? null;

  const habits = useMemo(() => computeHabits(selectedFund, behaviorId), [selectedFund, behaviorId]);
  const selectedHabit = habits.find((h) => h.id === habitId) ?? null;

  // Cada selección poda a sus dependientes y avanza la pestaña del banco a la categoría siguiente
  // — refuerza la sensación de progreso guiado que pide el rediseño, sin ocultar las demás pestañas.
  // Se lee el valor actual del estado en vez de la forma funcional (`setX(prev => …)`): cada
  // handler dispara una sola actualización por click, así que ambas formas son equivalentes aquí,
  // y la lectura directa evita anidar un ternario dentro de una función anidada (ver helpers arriba).
  // Estas mismas funciones se reutilizan como `onRemove*` desde `PostItCard` (toggle-OFF de un chip
  // ya puesto): la pestaña del banco solo debe avanzar en una selección real (toggle-ON), nunca al
  // quitar una palabra — de lo contrario, quitar un chip del post-it salta al usuario a la categoría
  // siguiente en vez de dejarlo donde estaba.
  const selectFundamental = (id: string) => {
    const isRemoving = fundamentalId === id;
    setFundamentalId(isRemoving ? null : id);
    setBehaviorId(null);
    setHabitId(null);
    if (!isRemoving) {
      setActiveTab(nextTabAfterFundamental(funds.find((entry) => entry.id === id)) ?? activeTab);
    }
  };
  const selectBehavior = (id: string) => {
    const isRemoving = behaviorId === id;
    setBehaviorId(isRemoving ? null : id);
    setHabitId(null);
    if (!isRemoving) {
      setActiveTab(nextTabAfterBehavior(selectedFund, id) ?? activeTab);
    }
  };
  const selectHabit = (id: string) => {
    const isRemoving = habitId === id;
    setHabitId(isRemoving ? null : id);
    if (!isRemoving) {
      setActiveTab('impacto');
    }
  };
  const selectImpact = (idx: number) => setImpactIdx(impactIdx === idx ? null : idx);

  // Click en un slot vacío del post-it (arriba): en vez de no hacer nada, salta a la pestaña del
  // banco que le corresponde y la lleva a la vista — el patrón de "espacio en blanco" del post-it
  // invita a clickear ahí primero, y sin esto el usuario no encontraba dónde elegir la palabra.
  const jumpToTab = (tab: BankTab) => {
    setActiveTab(tab);
    document.querySelector('.ri-bank')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const { behaviorRequired, habitRequired, totalSlots, filledSlots, isComplete } = computeSlotsProgress(
    selectedFund, hasAttitudes, habits.length, recipient, fundamentalId, behaviorId, habitId, impactIdx,
  );

  const { recipientLabel, fundLabel, fundColor, behaviorLabel, habitLabel, impactLabel } = resolveLabels(
    t, lang, recipient, selectedFund, fundIdx, selectedBehavior, selectedHabit, impactIdx,
  );

  const resetAll = () => {
    setRecipient(null);
    setFundamentalId(null);
    setBehaviorId(null);
    setHabitId(null);
    setImpactIdx(null);
    setMessage('');
    setAnonymous(false);
    setSendToBrillamos(false);
    setActiveTab('fundamentales');
  };

  const sentenceParts: SentenceParts = {
    recipientLabel, fundLabel, behaviorRequired, behaviorLabel, habitLabel, impactLabel,
  };

  // `combinedMessage` (frase armada por el post-it + lo escrito) sigue siendo el MENSAJE REAL que
  // se envía — el backend/Recap necesitan la narrativa completa, no solo la caja libre. Por eso el
  // TOPE de caracteres se mide contra él (y el post-it lo dice en su aviso): topar solo la caja
  // libre dejaba pasar envíos que el backend rechazaba por longitud.
  const combinedMessage = [buildRecognitionSentence(t, sentenceParts), message.trim()].filter(Boolean).join(' ');
  // El mínimo de caracteres, en cambio, se mide SOLO contra lo que el usuario escribe (igual que
  // `ReconocerPanel`, el formulario clásico): la frase automática del post-it no cuenta como texto
  // propio. Antes se medía sobre `combinedMessage`, así que la frase de plantilla por sí sola ya
  // cumplía el mínimo sin que el usuario escribiera nada — "Mín. caracteres" en Campañas quedaba
  // sin efecto real en este formulario, y el contador mostraba caracteres que el usuario no tecleó.
  const freeTextLength = message.trim().length;
  const messageOk = commentRequired
    ? freeTextLength >= Math.max(minCommentChars, 1)
    : freeTextLength === 0 || freeTextLength >= minCommentChars;
  const minCharsForHint = commentRequired ? Math.max(minCommentChars, 1) : minCommentChars;

  const doSend = () => {
    const body: CreateRecognitionBody = {
      receptorIds: recipient ? [recipient.id] : [],
      fundamentalIds: fundamentalId ? [fundamentalId] : [],
      mensaje: combinedMessage,
      behaviorIds: behaviorId ? [behaviorId] : [],
      habitIds: habitId ? [habitId] : [],
      // RN-PI03 — en /app/brillamos el post-it etiqueta origen Brillamos y adjunta la campaña; en
      // Fundamentales sigue siendo 'Diario' sin campaña (mismo criterio que ReconocerPanel).
      origen: isBrillamosScreen ? 'Brillamos' : 'Diario',
      campanaId: campaignId,
      // Defensa extra: `anonymous` puede haber quedado en `true` de un estado marcado antes de
      // que `rules` resolviera `anonymousAllowed` — el backend sigue siendo la autoridad real.
      anonimo: anonymousAllowed && anonymous,
      etiquetaGrupal: null,
      // T5 — solo se envía si el check está visible (gate de Gestión + Brillamos próxima) y marcado.
      sendToBrillamos: showSendToBrillamos && sendToBrillamos,
    };
    create.mutate(body, {
      onSuccess: () => {
        setSent(true);
        onSent?.();
        setTimeout(() => {
          setSent(false);
          resetAll();
        }, SENT_MS);
      },
    });
  };

  const guideSteps: GuideStep[] = [
    { targetSelector: '.ri-bank-tabs', title: t('ri.guideStep1Title'), text: t('ri.guideStep1Text') },
    { targetSelector: '.ri-sentence', title: t('ri.guideStep2Title'), text: t('ri.guideStep2Text') },
    { targetSelector: '.ri-postit-personal', title: t('ri.guideStep3Title'), text: t('ri.guideStep3Text') },
    { targetSelector: '.ri-send-btn', title: t('ri.guideStep4Title'), text: t('ri.guideStep4Text') },
  ];

  if (catalog.isLoading || catalog.isError || (catalog.data ?? []).length === 0) {
    return (
      <div className="ri-root">
        <StateBlock
          isLoading={catalog.isLoading}
          isError={catalog.isError}
          error={catalog.error}
          isEmpty={(catalog.data ?? []).length === 0}
          emptyText={t('catalog.empty')}
          onRetry={() => { catalog.refetch(); }}
        >
          <></>
        </StateBlock>
      </div>
    );
  }

  return (
    <div className="ri-root">
      <div className="ri-header">
        <ProgressDial
          value={filledSlots}
          total={totalSlots}
          label={t('ri.progressLabel', { value: filledSlots, total: totalSlots })}
        />
        <div className="ri-header-copy">
          <div className="ri-card-label"><span className="ri-dot" />{t('ri.msgLabel')}</div>
          <h2 className="ri-card-title">{t('ri.msgTitle')}</h2>
        </div>
        <button type="button" className="ri-guide-reopen" aria-label={t('ri.guideReopen')} onClick={() => setShowGuide(true)}>
          ?
        </button>
      </div>

      <WordBankTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        funds={funds}
        fundamentalId={fundamentalId}
        fundColor={fundColor}
        onSelectFundamental={selectFundamental}
        behaviorRequired={behaviorRequired}
        behaviors={behaviors}
        behaviorId={behaviorId}
        onSelectBehavior={selectBehavior}
        habits={habits}
        habitId={habitId}
        onSelectHabit={selectHabit}
        impactIdx={impactIdx}
        onSelectImpact={selectImpact}
        lang={lang}
      />

      <PostItCard
        recipient={recipient}
        onPickRecipient={setRecipient}
        onRemoveRecipient={() => setRecipient(null)}
        excludeIds={[demoUser.userId]}
        fundamentalId={fundamentalId}
        fundLabel={fundLabel}
        fundColor={fundColor}
        // PostItCard solo invoca cada onRemove* cuando su slot ya está lleno (ver `TemplateSlot`,
        // que solo llama a `onRemove` si `filled` es true) — el aserto no-nulo refleja ese contrato
        // en vez de repetir la misma guarda ya resuelta por el componente hijo.
        onRemoveFundamental={() => selectFundamental(fundamentalId as string)}
        onDropFundamental={selectFundamental}
        behaviorRequired={behaviorRequired}
        behaviorId={behaviorId}
        behaviorLabel={behaviorLabel}
        onRemoveBehavior={() => selectBehavior(behaviorId as string)}
        onDropBehavior={selectBehavior}
        habitRequired={habitRequired}
        habitId={habitId}
        habitLabel={habitLabel}
        onRemoveHabit={() => selectHabit(habitId as string)}
        onDropHabit={selectHabit}
        impactIdx={impactIdx}
        impactLabel={impactLabel}
        onRemoveImpact={() => selectImpact(impactIdx as number)}
        onDropImpact={selectImpact}
        hoveredSlot={hoveredSlot}
        onHoverSlot={setHoveredSlot}
        onJumpToTab={jumpToTab}
        message={message}
        onMessageChange={setMessage}
        anonymous={anonymousAllowed && anonymous}
        onAnonymousChange={setAnonymous}
        showAnonymous={anonymousAllowed}
        showSendToBrillamos={showSendToBrillamos}
        sendToBrillamos={sendToBrillamos}
        onSendToBrillamosChange={setSendToBrillamos}
        sendingDisabled={sendingDisabled}
        scheduleRestricted={scheduleRestricted}
        weeklySendLimitEffective={weeklySendLimitEffective}
        canSend={isComplete && !sendingDisabled && !scheduleRestricted && messageOk}
        isSending={create.isPending}
        sent={sent}
        onSend={doSend}
        onReset={resetAll}
        messageLength={freeTextLength}
        messageOk={messageOk}
        minChars={minCharsForHint}
        combinedLength={combinedMessage.length}
      />

      {create.isError && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" />
          <span>{recognitionSendErrorMessage(create.error, t, t('panel.sendErrorGeneric'))}</span>
        </p>
      )}

      <GuideOverlay
        steps={guideSteps}
        isOpen={showGuide}
        onClose={closeGuide}
        nextLabel={t('ri.guideNext')}
        skipLabel={t('ri.guideSkip')}
        doneLabel={t('ri.guideDone')}
      />
    </div>
  );
}

export default WordBuilderPanel;
